import * as React from "react";
import { useState, useEffect } from "react";
import { User } from "../models/users";
import { Comment, CommentJSON } from "../models/comments";
import { api } from "../api";
import { RouteComponentProps } from "react-router";
import { RLink } from "../DesignSystem/RLink";
import { TwoColumnView, Page } from "../DesignSystem/formatting/TwoColumnView";
import { FeedPanel } from "../DesignSystem/formatting/FeedComponents";
import { CommentComponent } from "../DesignSystem/CommentComponent";

interface AllCommentsViewProps
  extends RouteComponentProps<{ username: string }> {}

type CommentWithTitle = CommentJSON & { entry_title?: string };

// Flesch-Kincaid Grade Level calculation
const calculateFleschKincaid = (text: string): number => {
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const words = text.split(/\s+/).filter((w) => w.trim().length > 0);
  const syllables = words.reduce((count, word) => {
    return count + countSyllables(word);
  }, 0);

  if (sentences.length === 0 || words.length === 0) return 0;

  const avgWordsPerSentence = words.length / sentences.length;
  const avgSyllablesPerWord = syllables / words.length;

  // Flesch-Kincaid Grade Level formula
  const grade = 0.39 * avgWordsPerSentence + 11.8 * avgSyllablesPerWord - 15.59;
  return Math.max(0, Math.round(grade * 10) / 10);
};

// Count syllables in a word (approximation)
const countSyllables = (word: string): number => {
  word = word.toLowerCase().replace(/[^a-z]/g, "");
  if (word.length <= 3) return 1;

  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "");
  word = word.replace(/^y/, "");

  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Get grade level description
const getGradeDescription = (grade: number): string => {
  if (grade <= 1) return "Kindergarten";
  if (grade <= 5) return "Elementary School";
  if (grade <= 8) return "Middle School";
  if (grade <= 12) return "High School";
  if (grade <= 16) return "College";
  return "Graduate Level";
};

// Count thonk emojis
const countThonks = (text: string): number => {
  const thonkPattern = /:thonk:|:thinking:|:thonking:|🤔/gi;
  const matches = text.match(thonkPattern);
  return matches ? matches.length : 0;
};

// Stats panel component
const StatsPanel = ({ comments }: { comments: CommentWithTitle[] }) => {
  const allText = comments.map((c) => c.content).join(" ");
  const allWords = allText.split(/\s+/).filter((w) => w.trim().length > 0);
  const totalWords = allWords.length;
  const totalChars = allWords.join("").length;

  const avgWordsPerComment =
    comments.length > 0 ? Math.round(totalWords / comments.length) : 0;
  const avgWordLength =
    allWords.length > 0
      ? Math.round((totalChars / allWords.length) * 10) / 10
      : 0;
  const fleschKincaid = calculateFleschKincaid(allText);
  const thonkCount = countThonks(allText);

  const stats = [
    { label: "Total Comments", value: comments.length.toLocaleString() },
    { label: "Total Words Written", value: totalWords.toLocaleString() },
    {
      label: "Avg. Words per Comment",
      value: avgWordsPerComment.toLocaleString(),
    },
    { label: "Avg. Word Length", value: `${avgWordLength} chars` },
    {
      label: "Reading Level",
      value: `Grade ${fleschKincaid}`,
      subtitle: getGradeDescription(fleschKincaid),
    },
    { label: ":thonk: Emojis", value: thonkCount.toLocaleString() },
  ];

  return (
    <FeedPanel title={<>Comment Statistics</>}>
      <div className="space-y-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center justify-between text-sm"
          >
            <span className="text-slate-500 dark:text-slate-400">
              {stat.label}
            </span>
            <div className="text-right">
              <span className="font-medium">{stat.value}</span>
              {stat.subtitle && (
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  {stat.subtitle}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </FeedPanel>
  );
};

export function AllCommentsPage(props: AllCommentsViewProps) {
  const [profileUser, setProfileUser] = useState<User | undefined>(undefined);
  const [comments, setComments] = useState<CommentWithTitle[]>([]);

  const username = props.match.params.username;

  useEffect(() => {
    loadData();
  }, [username]);

  const loadData = async () => {
    // Fetch comments
    const commentsResult = await api.get<"/api/user/:username/comments">(
      `/api/user/${username}/comments`
    );
    if (!api.isToast(commentsResult)) {
      setComments(commentsResult);
    }

    // Fetch user info for avatar/name
    const userResult = await api.get<"/api/user/:username">(
      `/api/user/${username}`
    );
    if (!api.isToast(userResult)) {
      setProfileUser(new User(userResult.user));
    }
  };

  return (
    <Page>
      {/* Header outside of two-column view */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {username}'s comments
        </h1>
        <RLink
          to={`/user/${username}`}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Return to {username}'s page
        </RLink>
      </div>

      <TwoColumnView
        left={
          <div className="space-y-3">
            {profileUser &&
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800"
                >
                  <CommentComponent
                    anonymous="never"
                    comment_user={profileUser}
                    comment_likes={0}
                    comment_is_liked={false}
                    comment={new Comment(comment)}
                  />
                  <div className="px-4 pb-3 text-sm text-slate-500 dark:text-slate-400">
                    commented on{" "}
                    <RLink
                      to={`/entry/${comment.entry_id}`}
                      className="font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                    >
                      {comment.entry_title || `Entry #${comment.entry_id}`}
                    </RLink>
                  </div>
                </div>
              ))}
            {comments.length === 0 && (
              <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                No comments yet.
              </div>
            )}
          </div>
        }
        right={<StatsPanel comments={comments} />}
      />
    </Page>
  );
}
